import React, { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ArchiveOutline } from '@lyearn/icons';
import { AlertDialog } from '@lyearn/molecules';
import { useSnackbar } from '@lyearn/ui';
import useUpdateEntities from '@/modules/tasks/components/AddTasksModal/hooks/useUpdateEntities';
import { ActionItemFragment } from '@/modules/tasks/features/actionItems/graphql/fragments/generated/ActionItem';

import { DialogProps } from '../types';

function RemoveTaskFromOKR({ task, onClose, entity, entityId }: DialogProps) {
  const taskToBeRemoved = task as ActionItemFragment;

  const { t } = useTranslation(['Task']);
  const [isFetching, setIsFetching] = useState(false);
  const { showSnackbar } = useSnackbar();

  const { onRemoveEntities } = useUpdateEntities({
    entityId,
    entityType: entity,
  });

  const onRemove = useCallback(async () => {
    if (!taskToBeRemoved || !entityId || !entity) {
      return;
    }

    setIsFetching(true);
    try {
      await onRemoveEntities([taskToBeRemoved.id]);
      showSnackbar({
        message: t('Task:TaskWasRemoved'),
        startIcon: <ArchiveOutline className="h-20 w-20" fillColorToken="icon-placeholder" />,
      });
      onClose({ refetchData: true });
    } catch (error) {
      showSnackbar({
        message: t('Common:Error.SomethingWentWrong'),
        iconStatus: 'error',
      });
    } finally {
      setIsFetching(false);
    }
  }, [taskToBeRemoved, entityId, entity, onRemoveEntities, showSnackbar, t, onClose]);

  return (
    <AlertDialog
      header={{
        title: t('Task:RemoveTaskFromOKR'),
        description: t('Task:RemoveTaskFromOKRDescription'),
      }}
      primaryAction={{
        appearance: 'danger',
        label: t('Common:Actions.Remove'),
        onClick: onRemove,
        loading: isFetching,
      }}
      onClose={onClose}
    />
  );
}

export default RemoveTaskFromOKR;
